<script lang="ts" setup>
import { reactive, ref, onMounted, computed, watch, nextTick } from 'vue'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import esLocale from '@fullcalendar/core/locales/es'
import type { EventInput } from '@fullcalendar/core'
import AppEventDialog from './AppEventDialog.vue'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { useIndexedDB } from '@/composables/useIndexedDB'
import AppCalendarInfo from './AppCalendarInfo.vue'
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import AppInput from '@/components/atoms/inputs/AppInput.vue'

import {
  ChevronLeft,
  ChevronRight,
  Expand,
  FileText,
  MapPin,
  Search,
  Users,
} from 'lucide-vue-next'

const { t } = useI18n()
const toast = useToast()
const { initDB, getAllEvents, getEventById, addEvent, updateEvent, removeEvent } = useIndexedDB()
const slotMinTime = '07:00:00'
const slotMaxTime = '20:00:00'
const slotMinInputTime = slotMinTime.slice(0, 5)
const slotMaxInputTime = slotMaxTime.slice(0, 5)

defineProps<{
  compact?: boolean
}>()

export interface MyEvent extends EventInput {
  id: string
  title: string
  start: string
  end: string
  backgroundColor: string
  extendedProps: {
    idEvent: string
    nameEvent: string
    nameUser: string
    phoneNumber: string
    colorEvent: string
    descriptionEvent: string
    eventDuration: number
    isStarting?: boolean
  }
}

const showModal = ref(false)
const showExpandedCalendar = ref(false)
const searchOpen = ref(false)
const searchQuery = ref('')
const calendarTitle = ref('')
const expandedCalendarView = ref<'dayGridMonth' | 'timeGridWeek' | 'timeGridDay'>('timeGridWeek')
const compactSearchInput = ref<HTMLInputElement | null>(null)
const calendarRef = ref<InstanceType<typeof FullCalendar> | null>(null)
const expandedCalendarRef = ref<InstanceType<typeof FullCalendar> | null>(null)
const allEvents = ref<MyEvent[]>([])
const selectedExpandedEvent = ref<MyEvent | null>(null)
const fromDate = ref('')
const typeModal = ref<'create' | 'edit'>('create')
const editPayload = ref<any>(null)

const normalizeSearch = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const eventSearchText = (event: MyEvent) =>
  normalizeSearch(
    [
      event.title,
      event.extendedProps.nameUser,
      event.extendedProps.phoneNumber,
      event.extendedProps.descriptionEvent,
    ].join(' '),
  )

const filteredEvents = computed(() => {
  const query = normalizeSearch(searchQuery.value.trim())
  if (!query) return allEvents.value

  const words = query.split(/\s+/).filter(Boolean)
  return allEvents.value.filter((event) =>
    words.every((word) => eventSearchText(event).includes(word)),
  )
})

const currentDateLabel = computed(() =>
  new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
    .format(new Date())
    .replace(/ de /g, ' De '),
)

const compactCalendarTitle = computed(() => calendarTitle.value || currentDateLabel.value)

const formatEventTime = (value?: string) => {
  if (!value) return ''
  return new Intl.DateTimeFormat('es-CO', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(value))
}

const selectedEventDescription = computed(
  () =>
    selectedExpandedEvent.value?.extendedProps.descriptionEvent || 'Sin descripcion registrada.',
)

const selectedEventLocation = computed(() => {
  const description = selectedExpandedEvent.value?.extendedProps.descriptionEvent || ''
  const locationMatch = normalizeSearch(description).match(/(?:ubicacion|lugar):\s*([^|.\n]+)/i)
  return locationMatch?.[1]?.trim() || 'Sin ubicacion registrada'
})

const selectedEventAttendees = computed(
  () =>
    [selectedExpandedEvent.value?.extendedProps.nameUser].filter(Boolean).join(', ') ||
    'Sin asistentes registrados',
)

const datePartFromDateTime = (value: string) => value.split('T')[0]

const timePartFromDateTime = (value: string) => {
  const time = value.split('T')[1]
  return time ? time.slice(0, 5) : slotMinInputTime
}

const minutesFromTime = (value: string) => {
  const [hours, minutes] = value.split(':').map(Number)
  return hours * 60 + minutes
}

const formatDateTime = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}:00`
}

const buildDateTime = (date: string, time: string) => `${date}T${time}:00`

const isInsideCalendarRange = (startTime: string, duration: number) => {
  const start = minutesFromTime(startTime)
  const end = start + duration
  return start >= minutesFromTime(slotMinInputTime) && end <= minutesFromTime(slotMaxInputTime)
}

const getEventDuration = (start: Date, end: Date | null, fallbackDuration: number) => {
  if (!end) return fallbackDuration
  return (end.getTime() - start.getTime()) / 60000
}

const handleSelect = (info: any) => {
  if (info.allDay) {
    return
  }

  fromDate.value = info.startStr
  typeModal.value = 'create'
  showModal.value = true
}

const handleDateClick = (info: any) => {
  if (info.view.type !== 'dayGridMonth') return

  fromDate.value = buildDateTime(info.dateStr, slotMinInputTime)
  typeModal.value = 'create'
  showModal.value = true
}

const handleEventClick = (info: any) => {
  editPayload.value = info.event.extendedProps
  fromDate.value = info.event.startStr
  typeModal.value = 'edit'
  showModal.value = true
}

const handleExpandedEventClick = (info: any) => {
  selectedExpandedEvent.value = allEvents.value.find((event) => event.id === info.event.id) || null
  handleEventClick(info)
}

const openExpandedCalendar = async () => {
  showExpandedCalendar.value = true
  await refreshExpandedCalendarSize()
}

const toggleCompactSearch = async () => {
  if (searchOpen.value && !searchQuery.value) {
    searchOpen.value = false
    return
  }

  searchOpen.value = true
  await nextTick()
  compactSearchInput.value?.focus()
}

const closeExpandedCalendar = () => {
  showExpandedCalendar.value = false
  selectedExpandedEvent.value = null
}

const runCalendarAction = (action: 'today' | 'prev' | 'next') => {
  calendarRef.value?.getApi()[action]()
  expandedCalendarRef.value?.getApi()[action]()
}

const changeView = (view: 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay') => {
  expandedCalendarView.value = view
  calendarRef.value?.getApi().changeView(view)
  expandedCalendarRef.value?.getApi().changeView(view)
}

const refreshExpandedCalendarSize = async () => {
  await nextTick()

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      expandedCalendarRef.value?.getApi().updateSize()
    })
  })

  window.setTimeout(() => {
    expandedCalendarRef.value?.getApi().updateSize()
  }, 220)
}

const updateCalendarTitle = (info?: any) => {
  calendarTitle.value =
    info?.view?.title ||
    calendarRef.value?.getApi().view.title ||
    expandedCalendarRef.value?.getApi().view.title ||
    ''
}

const updateExpandedCalendarView = (info: any) => {
  expandedCalendarView.value = info.view.type
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

const readableTextColor = (color: string) => {
  const hex = color.replace('#', '')
  if (!/^[0-9a-f]{6}$/i.test(hex)) return '#ffffff'
  const red = Number.parseInt(hex.slice(0, 2), 16)
  const green = Number.parseInt(hex.slice(2, 4), 16)
  const blue = Number.parseInt(hex.slice(4, 6), 16)
  return (red * 299 + green * 587 + blue * 114) / 1000 > 150 ? '#111827' : '#ffffff'
}

const renderEventContent = (eventInfo: any) => {
  const event = eventInfo.event
  const time = eventInfo.timeText || formatEventTime(event.startStr)
  const contact = event.extendedProps.nameUser || ''
  const eventColor = event.backgroundColor || event.extendedProps.colorEvent || '#22c55e'
  const textColor = readableTextColor(eventColor)
  const style = `color:${textColor};`

  return {
    html: showExpandedCalendar.value
      ? `<div class="calendar-event-card" style="${style}"><strong>${escapeHtml(time)} - ${escapeHtml(
          event.title,
        )}</strong><span>${escapeHtml(contact)}</span></div>`
      : `<div class="calendar-event-compact" style="${style}"><strong>${escapeHtml(time)}</strong><span>${escapeHtml(
          event.title,
        )}${contact ? ` - ${escapeHtml(contact)}` : ''}</span></div>`,
  }
}

const handleEventDrop = async (info: any) => {
  const startDate = info.event.start as Date | null

  if (!startDate) {
    info.revert()
    return
  }

  const duration = getEventDuration(
    startDate,
    info.event.end as Date | null,
    info.event.extendedProps.eventDuration,
  )
  const start = formatDateTime(startDate)
  const end = formatDateTime(new Date(startDate.getTime() + duration * 60000))
  const timeEvent = timePartFromDateTime(start)

  if (info.event.allDay || !isInsideCalendarRange(timeEvent, duration)) {
    info.revert()
    toast.add({
      severity: 'warn',
      summary: t('general.warning'),
      detail: t('calendar.invalid_time_range', {
        start: slotMinInputTime,
        end: slotMaxInputTime,
      }),
      life: 4000,
    })
    return
  }

  try {
    const storedEvent = await getEventById(info.event.id)

    if (!storedEvent) {
      info.revert()
      return
    }

    await updateEvent({
      ...storedEvent,
      start,
      end,
    })
    await loadEvents()
  } catch {
    info.revert()
    toast.add({
      severity: 'error',
      summary: t('general.error'),
      detail: t('calendar.update_message_error'),
      life: 4000,
    })
  }
}

const calendarOptions = reactive({
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
  initialView: 'timeGridDay' as const,
  headerToolbar: {
    left: 'today,prev,next',
    center: '',
    right: 'dayGridMonth,timeGridWeek,timeGridDay',
  },
  locale: esLocale,
  slotMinTime,
  slotMaxTime,
  selectable: true,
  editable: true,
  eventStartEditable: true,
  eventDurationEditable: false,
  dragScroll: true,
  snapDuration: '00:05:00',
  events: [] as MyEvent[],
  eventContent: renderEventContent,
  datesSet: updateCalendarTitle,
  select: handleSelect,
  dateClick: handleDateClick,
  eventClick: handleEventClick,
  eventDrop: handleEventDrop,
  defaultTimedEventDuration: '00:10:00',
  views: {
    timeGrid: {
      slotDuration: '00:05:00',
      slotLabelInterval: '00:05',
      allDaySlot: false,
      now: new Date(),
      scrollTime: new Date().toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
      slotLabelFormat: {
        hour: '2-digit' as const,
        minute: '2-digit' as const,
        hour12: true,
      },
    },
  },
})

const expandedCalendarOptions = reactive({
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
  initialView: 'timeGridWeek' as const,
  headerToolbar: false as const,
  locale: esLocale,
  slotMinTime,
  slotMaxTime,
  selectable: true,
  editable: true,
  eventStartEditable: true,
  eventDurationEditable: false,
  dragScroll: true,
  snapDuration: '00:05:00',
  events: [] as MyEvent[],
  eventContent: renderEventContent,
  datesSet: updateExpandedCalendarView,
  select: handleSelect,
  dateClick: handleDateClick,
  eventClick: handleExpandedEventClick,
  eventDrop: handleEventDrop,
  defaultTimedEventDuration: '00:10:00',
  views: {
    timeGrid: {
      slotDuration: '00:05:00',
      slotLabelInterval: '00:05',
      allDaySlot: false,
      now: new Date(),
      scrollTime: new Date().toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
      slotLabelFormat: {
        hour: '2-digit' as const,
        minute: '2-digit' as const,
        hour12: true,
      },
    },
  },
})

watch(
  filteredEvents,
  (events) => {
    calendarOptions.events = events
    expandedCalendarOptions.events = events
    if (
      selectedExpandedEvent.value &&
      !events.some((event) => event.id === selectedExpandedEvent.value?.id)
    ) {
      selectedExpandedEvent.value = null
    }
  },
  { immediate: true },
)

watch(showExpandedCalendar, async (isOpen) => {
  if (!isOpen) return
  await refreshExpandedCalendarSize()
})

onMounted(async () => {
  await initDB()
  await loadEvents()
  updateCalendarTitle()
  setInterval(checkEventStart, 30000)
})

const loadEvents = async () => {
  const raw = await getAllEvents()
  allEvents.value = raw.map((e) => ({
    id: e.id,
    title: e.title,
    start: e.start,
    end: e.end,
    backgroundColor: e.color || '#22c55e',
    borderColor: e.color || '#22c55e',
    textColor: readableTextColor(e.color || '#22c55e'),
    extendedProps: {
      idEvent: e.id,
      nameEvent: e.title,
      nameUser: e.user_id,
      phoneNumber: e.description?.split('|')[0] || '',
      colorEvent: e.color || '#22c55e',
      descriptionEvent: e.description?.split('|')[1] || '',
      eventDuration: (new Date(e.end).getTime() - new Date(e.start).getTime()) / 60000,
    },
  }))
}

const saveEvent = async (data: any) => {
  const dateEvent = data.dateEvent || datePartFromDateTime(fromDate.value)
  const timeEvent = data.timeEvent || timePartFromDateTime(fromDate.value)

  if (!isInsideCalendarRange(timeEvent, data.eventDuration)) {
    toast.add({
      severity: 'warn',
      summary: t('general.warning'),
      detail: t('calendar.invalid_time_range', {
        start: slotMinInputTime,
        end: slotMaxInputTime,
      }),
      life: 4000,
    })
    return
  }

  const start = buildDateTime(dateEvent, timeEvent)
  const end = formatDateTime(new Date(new Date(start).getTime() + data.eventDuration * 60000))

  if (typeModal.value === 'create') {
    await addEvent({
      title: data.nameEvent,
      start,
      end,
      color: data.colorEvent,
      description: `${data.phoneNumber}|${data.descriptionEvent}`,
      user_id: data.nameUser,
    })
    toast.add({
      severity: 'success',
      summary: t('general.success'),
      detail: t('calendar.create_message_success'),
      life: 5000,
    })
  } else {
    await updateEvent({
      id: data.idEvent,
      title: data.nameEvent,
      start,
      end,
      color: data.colorEvent,
      description: `${data.phoneNumber}|${data.descriptionEvent}`,
      user_id: data.nameUser,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    toast.add({
      severity: 'success',
      summary: t('general.success'),
      detail: t('calendar.update_message_success'),
      life: 5000,
    })
  }
  await loadEvents()
}

const deleteEvent = async (idEvent: string) => {
  await removeEvent(idEvent)
  await loadEvents()
  toast.add({
    severity: 'success',
    summary: t('general.success'),
    detail: t('calendar.delete_message_success'),
    life: 5000,
  })
}

const checkEventStart = () => {
  const now = Date.now()
  allEvents.value.forEach((e) => {
    const start = new Date(e.start).getTime()
    if (!e.extendedProps.isStarting && now >= start - 30000 && now < start) {
      e.extendedProps.isStarting = true
      toast.add({
        severity: 'info',
        summary: t('calendar.pending_call'),
        detail: t('calendar.call_starting_soon', { event: e.title }),
        life: 5000,
      })
    }
  })
}
</script>

<template>
  <div class="calendar-shell h-full w-full" :class="{ 'calendar-shell--compact': compact }">
    <div class="calendar-topbar">
      <div class="montserrat font-semibold text-sm text-[var(--p-primary-color)]">{{ compactCalendarTitle }}</div>
      <div class="calendar-topbar-actions">
        <div class="calendar-search" :class="{ 'calendar-search--open': searchOpen || searchQuery }">
          <Button
            :class="[
              'inline-flex items-center cursor-pointer justify-center min-h-8 min-w-8 rounded-[0.45rem] transition-all duration-150 ease-in-out border',
              searchOpen
                ? ' text-[var(--p-primary-color)] border-none '
                : 'bg-[color-mix(in_srgb,var(--p-primary-color)_10%,transparent)] text-[var(--p-primary-color)] border-[color-mix(in_srgb,var(--p-primary-color)_18%,transparent)] hover:bg-[color-mix(in_srgb,var(--p-primary-color)_20%,transparent)]'
            ]"
            type="button"
            :input-class="searchOpen ? '!text-[var(--p-primary-color)] dark:!text-[var(--p-surface-100)]' : '!text-[var(--p-primary-color)]'"
            aria-label="Buscar eventos del calendario"
            @click="toggleCompactSearch"
          >
            <Search :size="16" />
          </Button>
          <input
            ref="compactSearchInput"
            v-model="searchQuery"
            placeholder="Buscar eventos del calendario..."
            class="text-xs dark:!text-white"
            @focus="searchOpen = true"
            @blur="searchOpen = Boolean(searchQuery)"
          />
        </div>
        <button
          class="inline-flex cursor-pointer items-center justify-center min-h-8 min-w-8 rounded-[0.45rem] bg-[color-mix(in_srgb,var(--p-primary-color)_10%,transparent)] border border-[color-mix(in_srgb,var(--p-primary-color)_18%,transparent)] text-[var(--p-primary-color)] transition-all duration-150 ease-in-out"
          type="button"
          aria-label="Expandir calendario"
          @click="openExpandedCalendar"
        >
          <Expand :size="16" />
        </button>
      </div>
    </div>

    <div class="calendar-main">
      <FullCalendar
        ref="calendarRef"
        colorScheme="dark"
        :options="calendarOptions"
        class="h-full w-full"
      />
    </div>
    <div class="fixed bottom-6 right-6 z-[90]">
      <AppCalendarInfo />
    </div>

    <AppDialog
      v-model="showExpandedCalendar"
      width="80vw"
      custom-class="calendar-expanded-dialog backdrop-blur-xl!"
      :dismissable-mask="true"
      :close-on-escape="!showModal"
      :draggable="false"
      :breakpoints="{ '1199px': '92vw', '575px': '96vw' }"
      @update:model-value="(visible) => !visible && closeExpandedCalendar()"
    >
      <template #header>
        <div class="calendar-expanded-header">
          <div class="flex flex-col gap-1">
            <!-- <label class="montserrat font-semibold text-md text-[var(--p-primary-color)]">Calendario Detallado</label> -->
            <label class="montserrat font-semibold text-md text-[var(--p-primary-color)]">{{ compactCalendarTitle }}</label>
          </div>
        </div>
      </template>

      <template #content>
        <section class="calendar-expanded-content">
          <div class="calendar-expanded-controls pt-1">
            <div class="calendar-control-group">
              <button type="button" class="cursor-pointer" @click="runCalendarAction('today')">Hoy</button>
              <button type="button" class="cursor-pointer" aria-label="Fecha anterior" @click="runCalendarAction('prev')">
                <ChevronLeft :size="18" />
              </button>
              <button type="button" aria-label="Fecha siguiente" class="cursor-pointer" @click="runCalendarAction('next')">
                <ChevronRight :size="18" />
              </button>
            </div>
            <div class="">
              <AppInput
                v-model="searchQuery"
                type="search"
                placeholder="Buscar eventos del calendario..."
                container-class="calendar-expanded-search-input"
                :show-error-message="false"
              >
                <template #icon>
                  <Search :size="14" class="text-[var(--p-primary-color)]" />
                </template> 
                </AppInput>
            </div>
            <div class="calendar-control-group ">
              <button
                type="button"
                :class="{ 'calendar-control-button--active ': expandedCalendarView === 'dayGridMonth' }"
                @click="changeView('dayGridMonth')"
                class="cursor-pointer"
                
              >
                Mes
              </button>
              <button
                type="button"
                :class="{ 'calendar-control-button--active ': expandedCalendarView === 'timeGridWeek' }"
                @click="changeView('timeGridWeek')"
                class="cursor-pointer "
              
              >
                Semana
              </button>
              <button
                type="button"
                :class="{ 'calendar-control-button--active ': expandedCalendarView === 'timeGridDay' }"
                @click="changeView('timeGridDay')"
                class="cursor-pointer"
              >
                Dia
              </button>
            </div>
          </div>

          <div class="calendar-expanded-body">
            <div class="calendar-expanded-grid">
              <FullCalendar
                ref="expandedCalendarRef"
                colorScheme="dark"
                :options="expandedCalendarOptions"
                class="h-full w-full"
              />
            </div>
            <aside class="border border-[var(--p-surface-100)] dark:border-[var(--p-surface-700)] rounded-lg p-3 flex-shrink-0 ">
              <template v-if="selectedExpandedEvent">
                <div
                  class="calendar-detail-color"
                  :style="{ backgroundColor: selectedExpandedEvent.backgroundColor }"
                ></div>
                <label class="montserrat font-medium text-md text-[var(--p-surface-900)] dark:text-[var(--p-surface-100)]">{{ selectedExpandedEvent.title }}</label class="montserrat font-semibold text-md text-[var(--p-primary-color)]">
                <p class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)]">
                  {{ formatEventTime(selectedExpandedEvent.start) }} -
                  {{ formatEventTime(selectedExpandedEvent.end) }}
                </p>
                <div class="calendar-detail-row">
                  <MapPin :size="17" />
                  <span class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)]">{{ selectedEventLocation }}</span>
                </div>
                <div class="calendar-detail-row">
                  <Users :size="17" />
                  <span class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)]">{{ selectedEventAttendees }}</span>
                </div>
                <div class="calendar-detail-row calendar-detail-description">
                  <FileText :size="17" />
                  <span class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)]">{{ selectedEventDescription }}</span>
                </div>
              </template>
              <template v-else>
                <label class="montserrat font-medium text-sm text-[var(--p-surface-800)] dark:text-[var(--p-surface-100)]">Detalle del evento</label>
                <p class="!text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-200)]">Selecciona un bloque del calendario para ver ubicacion, asistentes y descripcion.</p>
              </template>
            </aside>
          </div>
        </section>
      </template>
    </AppDialog>

    <AppEventDialog
      v-model:show="showModal"
      :type="typeModal"
      :dateInitial="fromDate"
      :minTime="slotMinInputTime"
      :maxTime="slotMaxInputTime"
      :editFormValue="editPayload"
      @save="saveEvent"
      @delete="deleteEvent"
    />
  </div>
</template>

<style lang="scss" scoped>
.calendar-shell,
.calendar-expanded-content,
:global(.calendar-expanded-dialog) {
  --calendar-text: var(--p-surface-500);
  --calendar-text-strong: var(--p-surface-900);
  --calendar-text-muted: var(--p-surface-600);
  --calendar-text-subtle: var(--p-surface-500); //Titulos de los dias y horas
  --calendar-border: color-mix(in srgb, var(--p-primary-color) 20%, transparent);

  --calendar-grid-bg: transparent !important;
  --calendar-header-bg: transparent !important;
  --calendar-panel-bg: transparent !important;
  --calendar-other-day-bg: color-mix(in srgb, var(--p-surface-50) 50%, transparent);
  --calendar-popover-bg: var(--p-surface-0);
  --calendar-popover-header-bg: var(--p-surface-50);

  --calendar-today-bg: color-mix(
    in srgb,
    var(--p-primary-color) 30%,
    var(--p-surface-0)
  );
  --calendar-hover-bg: color-mix(
    in srgb,
    var(--p-primary-color) 20%,
    var(--p-surface-0)
  );
  --calendar-highlight-bg: color-mix(
    in srgb,
    var(--p-primary-color) 18%,
    transparent
  );
  --calendar-list-hover-bg: color-mix(
    in srgb,
    var(--p-primary-color) 10%,
    var(--p-surface-0)
  );

  --calendar-detail-icon: var(--p-primary-color);
  --calendar-event-shadow: rgb(15 23 42 / 0.1);
  --calendar-event-hover-shadow: rgb(15 23 42 / 0.16);
}

html.dark .calendar-shell,
html.dark .calendar-expanded-content,
:global(html.dark .calendar-expanded-dialog) {
  --calendar-text: var(--p-surface-100);
  --calendar-text-strong: var(--p-surface-50);
  --calendar-text-muted: var(--p-surface-50); //Titulos de los dias y horas
  --calendar-text-subtle: var(--p-surface-200);
  --calendar-border: color-mix(
    in srgb,
    var(--p-primary-color) 20%,
    transparent
  );

  --calendar-grid-bg: transparent;
  --calendar-header-bg: transparent;
  --calendar-panel-bg: var(--p-surface-900);
  --calendar-other-day-bg: color-mix(in srgb, var(--p-surface-950) 60%, transparent);
  --calendar-popover-bg: var(--p-surface-900);
  --calendar-popover-header-bg: var(--p-surface-800);

  --calendar-today-bg: color-mix(
    in srgb,
    var(--p-primary-color) 30%,
    var(--p-surface-900)
  );
  --calendar-hover-bg: color-mix(
    in srgb,
    var(--p-primary-color) 24%,
    var(--p-surface-900)
  );
  --calendar-highlight-bg: color-mix(
    in srgb,
    var(--p-primary-color) 32%,
    transparent
  );
  --calendar-list-hover-bg: color-mix(
    in srgb,
    var(--p-primary-color) 18%,
    transparent
  );

  --calendar-detail-icon: var(--p-primary-300);
  --calendar-event-shadow: rgb(0 0 0 / 0.22);
  --calendar-event-hover-shadow: rgb(0 0 0 / 0.3);
}

/* -------------------------------------------------------------------------- */
/* Shell / compact calendar                                                    */
/* -------------------------------------------------------------------------- */

.calendar-shell {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 0;
  overflow: hidden;
  border-radius: 0.5rem;
  color: var(--calendar-text);
}

.calendar-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-height: 2.35rem;
}

.calendar-main {
  flex: 1;
  min-height: 0;
}

.calendar-date-label {
  color: var(--calendar-text);
  font-size: 0.95rem;
  font-weight: 600;
  text-transform: capitalize;
}

.calendar-topbar-actions {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

/* -------------------------------------------------------------------------- */
/* Buttons                                                                     */
/* -------------------------------------------------------------------------- */

.calendar-icon-button,
.calendar-control-group button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  min-height: 2rem;
  border: 1px solid color-mix(
    in srgb,
    var(--p-primary-color) 18%,
    transparent
  );
  border-radius: 0.45rem;
  background-color: color-mix(
    in srgb,
    var(--p-primary-color) 10%,
    transparent
  );
  color: var(--p-primary-color);
  transition:
    background-color 150ms ease,
    border-color 150ms ease,
    transform 150ms ease;
}

.calendar-icon-button:hover,
.calendar-control-group button:hover {
  border-color: color-mix(
    in srgb,
    var(--p-primary-color) 34%,
    transparent
  );
  background-color: color-mix(
    in srgb,
    var(--p-primary-color) 18%,
    transparent
  );
  transform: translateY(-1px);
}

.calendar-icon-button:active,
.calendar-control-group button:active {
  transform: translateY(0);
}

.calendar-icon-button:focus-visible,
.calendar-control-group button:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 0.18rem
    color-mix(in srgb, var(--p-primary-color) 24%, transparent);
}

.calendar-control-group {
  display: flex;
  gap: 0.4rem;
}

.calendar-control-group button {
  padding: 0.35rem 0.65rem;
  font-size: 0.82rem;
  font-weight: 600;
}

.calendar-control-group button.calendar-control-button--active {
  border-color: var(--p-primary-color);
  background-color: var(--p-primary-color);
  color: #fff;
  box-shadow: 0 8px 18px
    color-mix(in srgb, var(--p-primary-color) 22%, transparent);
}

.calendar-control-group button.calendar-control-button--active:hover {
  border-color: var(--p-primary-600);
  background-color: var(--p-primary-600);
}

/* -------------------------------------------------------------------------- */
/* Search                                                                      */
/* -------------------------------------------------------------------------- */

.calendar-search {
  display: grid;
  grid-template-columns: 2rem 0fr;
  align-items: center;
  overflow: hidden;
  border-bottom: 1px solid transparent;
  transition:
    border-color 220ms ease,
    grid-template-columns 260ms cubic-bezier(0.22, 1, 0.36, 1),
    filter 220ms ease;
}

.calendar-search input {
  min-width: 0;
  padding: 0.25rem 0;
  border: 0;
  outline: 0;
  background: transparent;
  caret-color: var(--p-primary-color);
  color: var(--calendar-text-strong);
  opacity: 0;
  transform: translateX(-0.35rem) scaleX(0.98);
  transform-origin: left center;
  transition:
    opacity 180ms ease,
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.calendar-search input:-webkit-autofill,
.calendar-search input:-webkit-autofill:hover,
.calendar-search input:-webkit-autofill:focus {
  -webkit-text-fill-color: var(--calendar-text-strong);
  transition: background-color 9999s ease-in-out 0s;
}

.calendar-search input::placeholder,
.calendar-expanded-search input::placeholder {
  color: var(--calendar-text-subtle);
}

.calendar-search--open {
  grid-template-columns: 2rem minmax(12rem, 1fr);
  border-color: color-mix(
    in srgb,
    var(--p-primary-color) 45%,
    transparent
  );
  filter: drop-shadow(
    0 8px 18px
      color-mix(in srgb, var(--p-primary-color) 10%, transparent)
  );
}

.calendar-search--open input {
  opacity: 1;
  transform: translateX(0) scaleX(1);
}

/* -------------------------------------------------------------------------- */
/* Expanded calendar                                                           */
/* -------------------------------------------------------------------------- */

.calendar-expanded-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: calc(90vh - 6.5rem);
  min-height: 36rem;
  color: var(--calendar-text);
}

.calendar-expanded-header,
.calendar-expanded-controls,
.calendar-expanded-body {
  min-width: 0;
}

.calendar-expanded-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.calendar-expanded-header h4 {
  margin: 0;
  color: var(--calendar-text-strong);
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
}

.calendar-expanded-header p {
  margin: 0.2rem 0 0;
  color: var(--calendar-text-muted);
  font-size: 0.9rem;
  text-transform: capitalize;
}

.calendar-expanded-controls {
  display: grid;
  grid-template-columns: max-content minmax(14rem, 1fr) max-content;
  align-items: center;
  gap: 0.85rem;
}

.calendar-expanded-search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.45rem 0.7rem;
  border: 1px solid var(--calendar-border);
  border-radius: 0.5rem;
  color: var(--p-primary-color);
}

.calendar-expanded-search:focus-within {
  border-color: color-mix(
    in srgb,
    var(--p-primary-color) 45%,
    transparent
  );
  box-shadow: 0 0 0 0.18rem
    color-mix(in srgb, var(--p-primary-color) 12%, transparent);
}

.calendar-expanded-search input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--calendar-text);
}

:global(.calendar-expanded-search-input) {
  flex: 1;
  min-width: 0;
}

.calendar-expanded-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(16rem, 20rem);
  flex: 1;
  gap: 1rem;
  overflow: hidden;
}

.calendar-expanded-grid,
.calendar-event-detail {
  min-height: 0;
  overflow: hidden;
  border-radius: 0.65rem;
}

.calendar-expanded-grid {
  padding: 0.5rem;
}

.calendar-event-detail {
  padding: 1rem;
  background-color: var(--calendar-panel-bg);
  color: var(--calendar-text);
}

.calendar-detail-color {
  width: 3.25rem;
  height: 0.35rem;
  margin-bottom: 0.9rem;
  border-radius: 999px;
}

.calendar-event-detail h3 {
  margin: 0 0 0.35rem;
  color: var(--calendar-text-strong);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.25;
}

.calendar-event-detail p,
.calendar-detail-row {
  color: var(--calendar-text-muted);
  font-size: 0.85rem;
}

.calendar-detail-time {
  margin: 0 0 1rem;
}

.calendar-detail-row {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin-top: 0.8rem;
}

.calendar-detail-row svg {
  flex: 0 0 auto;
  margin-top: 0.1rem;
  color: var(--calendar-detail-icon);
}

.calendar-detail-description {
  line-height: 1.45;
}

/* -------------------------------------------------------------------------- */
/* PrimeVue dialog                                                             */
/* -------------------------------------------------------------------------- */

:global(.calendar-expanded-dialog) {
  max-width: 1440px;
  border-color: var(--calendar-border) !important;
  background-color: transparent !important;
  color: var(--calendar-text);
}

:global(.calendar-expanded-dialog .p-dialog-header) {
  align-items: flex-start;
  padding-bottom: 0.75rem;
  color: var(--calendar-text);
}

:global(.calendar-expanded-dialog .p-dialog-content) {
  overflow: hidden;
  padding-bottom: 1.25rem;
  color: var(--calendar-text);
}

:global(.calendar-expanded-dialog .p-dialog-footer) {
  display: none;
}

:global(.calendar-expanded-dialog .p-dialog-close-button),
:global(.calendar-expanded-dialog .p-dialog-close-button:hover) {
  color: var(--calendar-text-strong);
}

/* -------------------------------------------------------------------------- */
/* FullCalendar                                                                */
/* -------------------------------------------------------------------------- */

:deep(.fc) {
  --fc-border-color: var(--calendar-border);
  --fc-button-bg-color: var(--p-primary-color);
  --fc-button-border-color: var(--p-primary-color);
  --fc-button-hover-bg-color: var(--p-primary-600);
  --fc-button-hover-border-color: var(--p-primary-600);
  --fc-button-active-bg-color: var(--p-primary-700);
  --fc-button-active-border-color: var(--p-primary-700);
  --fc-event-border-color: transparent;
  --fc-neutral-bg-color: var(--calendar-header-bg);
  --fc-page-bg-color: var(--calendar-grid-bg);
  --fc-today-bg-color: var(--calendar-today-bg);
  --fc-list-event-hover-bg-color: var(--calendar-list-hover-bg);

  height: 100%;
  color: var(--calendar-text);
  font-size: 0.82rem;

  .fc-toolbar {
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .fc-toolbar-chunk {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
    min-width: 0;
  }

  .fc-toolbar-title {
    color: var(--calendar-text-strong);
    font-size: 1rem;
    font-weight: 500;
    text-transform: capitalize;
  }

  .fc-button {
    min-height: 2rem;
    border-radius: 0.45rem;
    box-shadow: none;
    font-size: 0.76rem;
    font-weight: 500;
    text-transform: capitalize;
  }

  .fc-button:focus,
  .fc-button:active:focus {
    box-shadow: 0 0 0 0.18rem
      color-mix(in srgb, var(--p-primary-color) 24%, transparent);
  }

  .fc-button-primary:disabled {
    border-color: var(--p-surface-200);
    background-color: var(--p-surface-200);
    color: var(--p-surface-500);
    opacity: 1;
  }

  /*
   * FullCalendar paints several nested tables/cells. These are the selectors
   * that actually need an explicit theme surface; the previous long list was
   * redundant because most descendants inherit or are transparent already.
   */
  .fc-scrollgrid,
  .fc-scrollgrid-section > td,
  .fc-daygrid-day,
  .fc-timegrid-col,
  .fc-timegrid-slot,
  .fc-timegrid-slot-lane {
    background-color: var(--calendar-grid-bg) !important;
  }

  .fc-scrollgrid {
    overflow: hidden;
    border-radius: 0.5rem;
  }

  .fc-col-header-cell,
  .fc-timegrid-axis,
  .fc-timegrid-slot-label {
    background-color: var(--calendar-header-bg) !important;
  }

  .fc-day-other,
  .fc-day-disabled {
    background-color: var(--calendar-other-day-bg) !important;
  }

  .fc-day-today {
    background-color: var(--calendar-today-bg) !important;
  }

  .fc-daygrid-day:hover,
  .fc-timegrid-col:hover {
    background-color: var(--calendar-hover-bg) !important;
  }

  .fc-highlight {
    background-color: var(--calendar-highlight-bg);
  }

  .fc-col-header-cell-cushion,
  .fc-timegrid-slot-label-cushion {
    color: var(--calendar-text-muted);
    font-size: 0.73rem;
    font-weight: 500;
    text-decoration: none;
    text-transform: uppercase;
  }

  .fc-daygrid-day-number {
    color: var(--calendar-text);
    font-size: 0.78rem;
    font-weight: 500;
    text-decoration: none;
  }

  .fc-event {
    overflow: hidden;
    border-color: var(--fc-event-border-color) !important;
    border-radius: 0.4rem;
    background-color: var(--fc-event-bg-color) !important;
    box-shadow: 0 6px 14px var(--calendar-event-shadow);
    cursor: grab;
    font-weight: 500;
    transition:
      transform 150ms ease,
      box-shadow 150ms ease,
      filter 150ms ease;
  }

  .fc-event:hover {
    box-shadow: 0 8px 18px var(--calendar-event-hover-shadow);
    filter: brightness(0.96);
    transform: translateY(-1px);
  }

  .fc-event:active {
    cursor: grabbing;
  }

  .fc-event-main,
  .fc-event-main-frame,
  .fc-event-title-container,
  .fc-event-title {
    width: 100%;
    height: 100%;
  }

  .fc-daygrid-event {
    padding: 0.08rem 0.2rem;
  }

  .fc-timegrid-event {
    padding: 0.12rem 0.16rem;
  }

  .fc-event-title,
  .fc-event-time {
    line-height: 1.2;
  }

  .calendar-event-compact,
  .calendar-event-card {
    display: flex;
    flex-direction: column;
    gap: 0.08rem;
    width: 100%;
    height: 100%;
    min-width: 0;
    overflow: hidden;
    padding: 0.08rem 0.12rem;
    border: 0;
    border-radius: 0.38rem;
  }

  .calendar-event-compact strong,
  .calendar-event-card strong,
  .calendar-event-compact span,
  .calendar-event-card span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .calendar-event-compact strong,
  .calendar-event-card strong {
    color: inherit;
    font-size: 0.72rem;
    font-weight: 700;
  }

  .calendar-event-compact span,
  .calendar-event-card span {
    color: inherit;
    font-size: 0.7rem;
    opacity: 0.9;
  }

  .calendar-event-card {
    gap: 0.16rem;
    padding: 0.18rem 0.2rem;
  }

  .calendar-event-card strong {
    font-size: 0.8rem;
  }

  .fc-list-event:hover td {
    background-color: var(--calendar-list-hover-bg);
  }

  .fc-popover {
    border-color: var(--calendar-border) !important;
    background-color: var(--calendar-popover-bg) !important;
    color: var(--calendar-text);
  }

  .fc-popover-header {
    background-color: var(--calendar-popover-header-bg) !important;
    color: var(--calendar-text-strong) !important;
  }
}

/* Compact variant: only differences from the base FullCalendar rules. */
.calendar-shell--compact {
  :deep(.fc) {
    .fc-header-toolbar {
      margin-bottom: 0.65rem;
    }

    .fc-toolbar-title {
      font-size: 0.92rem;
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Responsive                                                                  */
/* -------------------------------------------------------------------------- */

@media (max-width: 980px) {
  .calendar-expanded-content {
    height: calc(94vh - 6.5rem);
  }

  .calendar-expanded-controls,
  .calendar-expanded-body {
    grid-template-columns: 1fr;
  }

  .calendar-expanded-body {
    overflow: auto;
  }

  .calendar-expanded-grid {
    min-height: 34rem;
  }
}
</style>
